import { fetchApi } from '@/services/api-client';

export const CELEBRATIONS_PATH = '/celebrations';

export type CelebrationStatus = 'draft' | 'published' | 'archived';

export type Celebration = {
  id: string;
  title: string;
  periodStart: string;
  periodEnd: string;
  schoolYearId: string;
  schoolYearCode: string | null;
  schoolYearName: string | null;
  status: CelebrationStatus;
  notes: string | null;
  /** Fewest sand dollars in the window that earns a prize; null when none is set. */
  minimumDollars: number | null;
  /** School-time "HH:mm" on the last day when the count closes; null closes it at the end of the day. */
  endTime: string | null;
  createdAt: string;
  updatedAt: string;
};

export type StandingEntry = {
  studentId: string;
  displayName: string;
  photoUrl: string;
  gradeLevel: number;
  houseId: string | null;
  houseName: string | null;
  houseColor: string | null;
  total: number;
  rank: number;
};

export type StandingsResponse = {
  celebration: Celebration;
  overall: StandingEntry[];
  byHouse: Record<string, StandingEntry[]>;
  byGrade: Record<string, StandingEntry[]>;
  houses: { id: string; name: string; color: string | null }[];
  grades: number[];
  /** Every enrolled child, either side of the minimum; null when the celebration sets none. */
  minimum: { threshold: number; reached: StandingEntry[]; notReached: StandingEntry[] } | null;
};

export const celebrationsApi = {
  list: () => fetchApi<Celebration[]>(CELEBRATIONS_PATH),
  getById: (id: string) => fetchApi<Celebration>(`${CELEBRATIONS_PATH}/${id}`),
  create: (data: {
    title: string;
    periodStart: string;
    periodEnd: string;
    status?: CelebrationStatus;
    notes?: string;
    minimumDollars?: number | null;
    endTime?: string | null;
  }) => fetchApi<Celebration>(CELEBRATIONS_PATH, { method: 'POST', body: JSON.stringify(data) }),
  update: (
    id: string,
    data: Partial<{
      title: string;
      periodStart: string;
      periodEnd: string;
      status: CelebrationStatus;
      notes: string | null;
      minimumDollars: number | null;
      endTime: string | null;
    }>
  ) =>
    fetchApi<Celebration>(`${CELEBRATIONS_PATH}/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  delete: (id: string) => fetchApi<{ success: boolean }>(`${CELEBRATIONS_PATH}/${id}`, { method: 'DELETE' }),
  getStandings: (id: string) => fetchApi<StandingsResponse>(`${CELEBRATIONS_PATH}/${id}/standings`),
};
